import type { NativeSale } from '../../types/nativeSales';
import { formatMoney } from '../../utils/costFormatting';
import { describePendingRecovery } from '../../utils/nativePos';
import { describeProducts } from '../../utils/posView';
import { Badge, Button } from '../ui/primitives';

// Rediseño · Bloque D — las ventas que quedaron guardadas sin cobrar (recarga, cierre de la app, sin
// señal, o un cobro con lector que no terminó). Las pantallas aprobadas no las dibujan; son reales y
// se conservan enteras: aquí están TODAS las de la tienda, sin recorte y sin plegar. Una venta que
// deja de verse no deja de existir, y es justo la que nadie va a resolver.
//
// FI-53: entre ellas puede haber una que YA tiene el cobro con tarjeta confirmado y se quedó sin
// completar. De esa no se dice «sin cobrar», lleva su propia marca y no ofrece «Cancelar»: se abre
// con «Continuar», que la completa.
//
// Solo dibuja. Qué ventas son, cuál está en riesgo y quién puede cerrar una declarando lo decide
// quien usa este componente, con lo que dijo el servidor.

export function PosRecovery({ sales, detail, flag, atRisk, paid, paidFlag, busy, resumingSaleId, timeOf, canDeclare, onResume, onCancel, onDeclare }: {
  sales: NativeSale[];
  /** La frase que dice qué se puede afirmar de estas ventas (ver `describeRecoverableCardRisk`). */
  detail: string;
  /** La marca de una venta que intentó cobrarse con el lector. */
  flag: string;
  atRisk: ReadonlySet<string>;
  /** Las ventas cuyo cobro el servidor ya confirmó y que siguen sin completar. */
  paid: ReadonlySet<string>;
  /** La marca de esas ventas. */
  paidFlag: string;
  busy: boolean;
  /** La venta que está preguntando al servidor cómo quedó, si hay una. */
  resumingSaleId: string | undefined;
  /** La hora de la venta, en la zona de SU tienda. */
  timeOf: (sale: NativeSale) => string;
  canDeclare: (sale: NativeSale) => boolean;
  onResume: (sale: NativeSale) => void;
  onCancel: (sale: NativeSale) => void;
  onDeclare: (sale: NativeSale) => void;
}) {
  return (
    <section aria-labelledby="pos-recovery-title" className="n3-pos__recovery">
      <div className="n3-pos__recovery-head" role="status">
        <h2 className="n3-pos__recovery-title" id="pos-recovery-title">{describePendingRecovery(sales.length, paid.size > 0)}</h2>
        <p className="n3-pos__recovery-detail">{detail}</p>
      </div>
      <ul className="n3-pos__recovery-list">
        {sales.map((sale) => (
          <li className="n3-pos__recovery-item" key={sale.id}>
            <div className="n3-pos__recovery-info">
              <strong className="n3-pos__recovery-total">{formatMoney(sale.totalAmount)}</strong>
              <span className="n3-pos__recovery-when">{timeOf(sale)} · {describeProducts(sale.lines.length)}</span>
              {paid.has(sale.id) && <Badge tone="warning">{paidFlag}</Badge>}
              {atRisk.has(sale.id) && <Badge tone="warning">{flag}</Badge>}
            </div>
            <div className="n3-pos__recovery-actions">
              <Button className="n3-pos__recovery-action" disabled={busy || resumingSaleId !== undefined} onClick={() => onResume(sale)} variant="primary">
                {resumingSaleId === sale.id ? 'Abriendo…' : 'Continuar'}
              </Button>
              {/* Una venta ya cobrada no se cancela: el servidor lo niega, y el botón no debe invitar. */}
              {!paid.has(sale.id) && (
                <Button className="n3-pos__recovery-action" disabled={busy} onClick={() => onCancel(sale)} variant="danger">Cancelar</Button>
              )}
              {canDeclare(sale) && (
                <Button className="n3-pos__recovery-action" disabled={busy} onClick={() => onDeclare(sale)} variant="ghost">Cerrar declarando…</Button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
